/**
 * SignalR hub connection management.
 *
 * Three hub paths exist server-side (§7 + §15.4 of the plan):
 *   /hubs/instances   — per-account push for algorithm state + decisions + market updates
 *   /hubs/dashboard   — per-account "summary changed, refetch" nudge
 *   /hubs/admin/audit — admin-only stream of new audit_log rows
 *
 * The browser cannot attach headers to WS handshakes, so the server reads
 * `?access_token=` for any `/hubs/*` path, and — for the same reason — the
 * account a connection acts for rides as `?account=` when the person has
 * switched to an organization (ai_docs/corporate-user-feature.md §6.1). The
 * server joins the connection to that account's groups at connect time. The
 * token comes from an async `accessTokenFactory` (MSAL acquires it silently)
 * — and when MSAL cannot produce one, the factory THROWS instead of sending an
 * empty bearer. Overnight tabs used to negotiate with dead tokens forever; two
 * hubs at a 30s retry cap flipped prod EB Severe on pure 401 noise (2026-07-11).
 *
 * `manageHubConnection` owns the connection lifecycle end to end — initial
 * start, restart after a drop, capped exponential backoff with jitter — with
 * three gates the raw retry machinery lacked:
 *   - visibility: a hidden tab never dials, and pending retries park until it
 *     is visible again (matches React Query's background behavior);
 *   - auth circuit breaker: consecutive auth failures retire the session, and
 *     `AuthProvider` routes to sign-in, instead of retrying forever;
 *   - account access: a 403 at negotiate for a delegated connection means the
 *     grant is gone, not the session — reported once, never retried, and never
 *     counted against the auth breaker.
 *
 * A session MSAL says is over (`interaction-required`) short-circuits all of
 * that: no token will ever arrive, so the loop stops on the first attempt
 * rather than burning the three-strike budget on backoff the user sits through
 * (ticket #33).
 *
 * `LongPolling` is forced ONLY in tests (TestServer doesn't speak WebSockets);
 * in the browser we leave the default transport selection alone so SignalR
 * picks WebSockets when available and falls back gracefully.
 */
import { HttpError, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import type { HubConnection } from '@microsoft/signalr'
import { acquireApiToken, endSession } from './msal'
import { reportAccountAccessLost } from './activeAccount'

export type HubKind = 'instances' | 'dashboard' | 'adminAudit'

const HUB_PATHS: Record<HubKind, string> = {
    instances: '/hubs/instances',
    dashboard: '/hubs/dashboard',
    adminAudit: '/hubs/admin/audit',
}

/** Consecutive auth failures before a connection stops retrying and retires the
 *  session. Non-auth failures (deploy, network blip) reset the count. */
const AUTH_FAILURE_LIMIT = 3

/** MSAL could not mint a token right now, but the session may still be alive
 *  (offline, network fault). Worth retrying under the usual backoff. */
export class AuthTokenUnavailableError extends Error {
    constructor() {
        super('No access token available for hub connection')
        this.name = 'AuthTokenUnavailableError'
    }
}

/** The session is over — nobody is signed in, or only an interactive sign-in
 *  can produce a token. Retrying cannot help, so the loop stops dead. */
export class SessionEndedError extends Error {
    constructor() {
        super('Session ended; hub connection cannot be authenticated')
        this.name = 'SessionEndedError'
    }
}

/** Auth-shaped failures: MSAL produced no token, or the server rejected the
 *  one we sent. SignalR wraps negotiate errors (including a throwing token
 *  factory) into plain Errors whose message embeds the original, so match the
 *  embedded markers as well as the typed forms. */
const isAuthFailure = (err: unknown): boolean =>
    err instanceof AuthTokenUnavailableError ||
    (err instanceof HttpError && err.statusCode === 401) ||
    (err instanceof Error &&
        (err.message.includes("Status code '401'") ||
            err.message.includes('AuthTokenUnavailableError')))

const isSessionEnded = (err: unknown): boolean =>
    err instanceof SessionEndedError ||
    (err instanceof Error && err.message.includes('SessionEndedError'))

/** The server refused the account named on the negotiate — the only way a
 *  signed, provisioned connection to a per-account hub earns a 403. */
const isAccountForbidden = (err: unknown): boolean =>
    (err instanceof HttpError && err.statusCode === 403) ||
    (err instanceof Error && err.message.includes("Status code '403'"))

/**
 * Token for a hub handshake, or a typed throw the caller can act on. A dead
 * session is retired here so the UI learns about it from whichever consumer
 * noticed first — a hub is often the earliest, since it re-dials on its own.
 */
const acquireHubToken = async (): Promise<string> => {
    const result = await acquireApiToken()
    if (result.kind === 'token') return result.token
    if (result.kind === 'interaction-required') {
        void endSession()
        throw new SessionEndedError()
    }
    if (result.kind === 'no-account') throw new SessionEndedError()
    throw new AuthTokenUnavailableError()
}

const hubUrl = (kind: HubKind, accountId: string | null | undefined): string =>
    accountId ? `${HUB_PATHS[kind]}?account=${encodeURIComponent(accountId)}` : HUB_PATHS[kind]

const createHubConnection = (kind: HubKind, accountId: string | null | undefined): HubConnection =>
    new HubConnectionBuilder()
        .withUrl(hubUrl(kind, accountId), { accessTokenFactory: acquireHubToken })
        .configureLogging(LogLevel.Warning)
        .build()

/**
 * Idempotently start a HubConnection. If the connection is already
 * Connected/Connecting we return immediately — re-issuing StartAsync on a
 * Connected connection throws an InvalidOperationException.
 */
const ensureStarted = async (connection: HubConnection): Promise<void> => {
    if (
        connection.state === HubConnectionState.Connected ||
        connection.state === HubConnectionState.Connecting ||
        connection.state === HubConnectionState.Reconnecting
    ) {
        return
    }
    await connection.start()
}

const backoffMs = (attempt: number): number =>
    Math.min(30_000, 1_000 * 2 ** attempt) + Math.floor(Math.random() * 1_000)

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

export type ManagedHubOptions = {
    /** Register `.on(...)` handlers here — runs before the first start so no
     *  early server push is missed. */
    configure?: (connection: HubConnection) => void
    /** Fires after every successful (re)start EXCEPT the first: the gap means
     *  missed pushes, so consumers resync their hub-fed caches. */
    onReconnected?: () => void
    /** The organization this connection acts for (`?account=`); absent or
     *  null for the person's own account. */
    accountId?: string | null
}

/**
 * Create, start, and babysit a hub connection for its whole life: one owner
 * for the initial start AND every restart after a drop (a dashboard left open
 * must outlive an EB deploy or a network blip). Returns the dispose function
 * for the owning effect's cleanup.
 */
export const manageHubConnection = (
    kind: HubKind,
    options: ManagedHubOptions = {},
): (() => void) => {
    const connection = createHubConnection(kind, options.accountId)
    options.configure?.(connection)

    let disposed = false
    let everConnected = false
    let authFailures = 0
    let releaseVisibilityWait: (() => void) | null = null

    const waitForVisible = (): Promise<void> =>
        new Promise((resolve) => {
            if (disposed || document.visibilityState === 'visible') {
                resolve()
                return
            }
            const settle = () => {
                document.removeEventListener('visibilitychange', onChange)
                releaseVisibilityWait = null
                resolve()
            }
            const onChange = () => {
                if (document.visibilityState === 'visible') settle()
            }
            releaseVisibilityWait = settle
            document.addEventListener('visibilitychange', onChange)
        })

    const run = async (): Promise<void> => {
        for (let attempt = 0; !disposed; attempt++) {
            if (document.visibilityState === 'hidden') await waitForVisible()
            if (disposed) return
            try {
                // Preflight the token OUTSIDE SignalR so a dead MSAL session
                // surfaces as the typed error (SignalR would wrap it) and a
                // tokenless negotiate never reaches the server at all.
                await acquireHubToken()
                await ensureStarted(connection)
                authFailures = 0
                if (everConnected) options.onReconnected?.()
                everConnected = true
                return
            } catch (err) {
                // No amount of waiting fixes a session that is over.
                if (isSessionEnded(err)) return
                // Nor an account we may no longer act for. The negotiate error
                // carries no body, so this cannot tell a removed grant from the
                // operator's kill switch; the REST layer, which reads the body,
                // normally reports first and the provider ignores the repeat.
                if (options.accountId && isAccountForbidden(err)) {
                    reportAccountAccessLost(options.accountId, 'removed')
                    return
                }
                if (isAuthFailure(err)) {
                    authFailures += 1
                    if (authFailures >= AUTH_FAILURE_LIMIT) {
                        void endSession()
                        return
                    }
                } else {
                    authFailures = 0
                }
                await delay(backoffMs(attempt))
            }
        }
    }

    connection.onclose(() => {
        if (!disposed) void run()
    })
    void run()

    return () => {
        disposed = true
        releaseVisibilityWait?.()
        void connection.stop()
    }
}
